import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  ErrorIcon,
  ExportFailedIcon,
  ExportedIcon,
  ProcessingIcon,
  ReviewedIcon,
  ToReviewIcon,
} from "../status/invoice";

type Props = {
  status: InvoiceStatus;
  onSelect?: (status: InvoiceStatus) => void;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  children?: (value: {
    icon: React.ReactNode;
    color?: string;
    statusText?: string;
  }) => React.ReactNode;
  disableDropdown?: boolean;
};

export const StatusSelect = ({
  status,
  onSelect = noop,
  className,
  children,
  disableDropdown = false,
}: Props) => {
  const { t } = useTranslation();
  let icon;
  let color;
  let statusText;

  switch (status) {
    case InvoiceStatus.EXPORTED:
      icon = <ExportedIcon />;
      break;
    case InvoiceStatus.REVIEWED:
      icon = <ReviewedIcon />;
      break;
    case InvoiceStatus.PREPROCESSED:
      icon = <ToReviewIcon />;
      break;
    case InvoiceStatus.ERROR:
      icon = <ErrorIcon />;
      break;
    case InvoiceStatus.EXPORT_FAILED:
      icon = <ExportFailedIcon />;
      break;
    case InvoiceStatus.NEW:
      icon = <ProcessingIcon />;
      break;
    default:
      icon = null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        className={className}
        disabled={disableDropdown || status === InvoiceStatus.ERROR}
      >
        {children ? (
          children({ icon, color, statusText })
        ) : (
          <div style={{ color: color }}>{icon}</div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={status === InvoiceStatus.PREPROCESSED}
          onCheckedChange={() => onSelect(InvoiceStatus.PREPROCESSED)}
          className="gap-2"
        >
          <ToReviewIcon />
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={status === InvoiceStatus.REVIEWED}
          onCheckedChange={() => onSelect(InvoiceStatus.REVIEWED)}
          className="gap-2"
        >
          <ReviewedIcon />
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={status === InvoiceStatus.EXPORTED}
          onCheckedChange={() => onSelect(InvoiceStatus.EXPORTED)}
          className="gap-2"
        >
          <ExportedIcon />
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
